import React, { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { MapPin, AlertTriangle, Wand2 } from "lucide-react";
import { Button } from "./ui/button";
import { HolderNormalizeDialog } from "./HolderNormalizeDialog";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const CATS = [
  { key: "merkez", label: "Seçim Ofisinde", cls: "text-[#0F2C59] dark:text-sky-300" },
  { key: "sube", label: "Şubede", cls: "text-violet-600" },
  { key: "kendi", label: "Kendi Aldı", cls: "text-amber-600" },
  { key: "kisi", label: "Kişide", cls: "text-emerald-600" },
  { key: "diger", label: "Diğer", cls: "text-cyan-600" },
  { key: "bos", label: "Girilmemiş", cls: "text-muted-foreground" },
];

export const HolderSummary = ({ committeeNo, location, branch, refresh, onLocationChange, onBranchChange }) => {
  const [data, setData] = useState(null);
  const [normOpen, setNormOpen] = useState(false);
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;

  useEffect(() => {
    setData(null);
    api.get("/reports/document-holders", {
      params: {
        ...(committeeNo && committeeNo !== "all" ? { committee_no: Number(committeeNo) } : {}),
        ...(location !== "all" ? { location } : {}),
        ...(location === "sube" && branch !== "all" ? { branch } : {}),
      },
    }).then(({ data }) => setData(data)).catch(() => setData({ rows: [], categories: {} }));
  }, [committeeNo, location, branch, refresh]);

  if (!data) return <Skeleton className="h-52 w-full" />;

  const cats = data.categories || {};
  const total = CATS.reduce((s, c) => s + (cats[c.key] || 0), 0);
  const top = (data.rows || []).slice(0, 12);
  const max = Math.max(1, ...top.map((r) => r.count));

  return (
    <div className="space-y-3" data-testid="holder-summary">
      <div className="flex items-center gap-2 flex-wrap">
        <MapPin className="h-4 w-4 text-violet-600" />
        <p className="text-sm font-medium">Belge nerede / kimde?</p>
        <Select value={location} onValueChange={onLocationChange}>
          <SelectTrigger className="h-9 w-[190px]" data-testid="holder-location-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="merkez">Seçim Ofisinde</SelectItem>
            <SelectItem value="sube">Şubede</SelectItem>
            <SelectItem value="kendi">Kendi Aldı</SelectItem>
            <SelectItem value="kisi">Kişide</SelectItem>
            <SelectItem value="diger">Diğer</SelectItem>
            <SelectItem value="bos">Girilmemiş</SelectItem>
          </SelectContent>
        </Select>
        {isOwner && (
          <>
            <Button variant="outline" size="sm" className="h-9" onClick={() => setNormOpen(true)} data-testid="holder-normalize-button">
              <Wand2 className="h-4 w-4 mr-1.5" />Yazım Düzelt
            </Button>
            <HolderNormalizeDialog open={normOpen} onOpenChange={setNormOpen} onDone={() => onLocationChange(location)} />
          </>
        )}
        {location === "sube" && (
          <Select value={branch} onValueChange={onBranchChange}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="holder-branch-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tüm şubeler</SelectItem>
              {(data.available_branches || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {(cats.bos || 0) > 0 && location !== "bos" && (
        <Card className="p-3 border-amber-400 bg-amber-50 dark:bg-amber-950/30 flex items-center gap-3 flex-wrap" data-testid="holder-missing-warning">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {cats.bos} firmada belge alınmış ama <b>nerede/kimde olduğu girilmemiş</b>
            </p>
            <p className="text-xs text-amber-700/80 dark:text-amber-400/80">
              Seçim günü belgeyi bulamamak için en büyük risk. Listeleyip işaretleyin.
            </p>
          </div>
          <button type="button" onClick={() => onLocationChange("bos")} data-testid="holder-missing-list"
            className="text-xs font-medium px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700">
            Listele ve işaretle
          </button>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {CATS.map((c) => (
          <Card key={c.key} className={`p-3 cursor-pointer transition-colors ${location === c.key ? "border-violet-500" : "hover:bg-muted/40"}`}
            onClick={() => onLocationChange(location === c.key ? "all" : c.key)} data-testid={`holder-card-${c.key}`}>
            <div className={`text-xl font-bold tabular-nums ${c.cls}`}>{cats[c.key] || 0}</div>
            <div className="text-xs text-muted-foreground">{c.label}</div>
            <div className="text-[10px] text-muted-foreground">{total ? Math.round((cats[c.key] || 0) / total * 100) : 0}%</div>
          </Card>
        ))}
      </div>

      {(data.branches || []).length > 0 && (
        <Card className="p-3" data-testid="holder-branch-breakdown">
          <p className="text-xs font-medium mb-2">Şube kırılımı</p>
          <div className="flex flex-wrap gap-2">
            {data.branches.map((b) => (
              <button key={b.branch} type="button" onClick={() => { onLocationChange("sube"); onBranchChange(b.branch); }}
                className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300 hover:opacity-80"
                data-testid={`holder-branch-${b.branch}`}>
                {b.branch}: <b className="tabular-nums">{b.count}</b>
              </button>
            ))}
          </div>
        </Card>
      )}

      {top.length > 0 && (
        <Card className="p-3" data-testid="holder-chart">
          <p className="text-xs font-medium mb-2">Kimde kaç belge var (en çok tutandan aza)</p>
          <div className="space-y-1.5">
            {top.map((r) => (
              <div key={r.holder} className="flex items-center gap-2" data-testid={`holder-bar-${r.holder}`}>
                <span className="text-xs w-44 sm:w-56 truncate" title={r.holder}>{r.holder}</span>
                <div className="flex-1 h-4 rounded bg-muted overflow-hidden">
                  <div className="h-full bg-[#0F2C59] dark:bg-sky-500" style={{ width: `${r.count / max * 100}%` }} />
                </div>
                <span className="text-xs font-semibold tabular-nums w-10 text-right">{r.count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default HolderSummary;
